import { GORef } from "./goref";
import { GameObject } from "./gameobject";
import { StopCmd } from "./commands/stop";
import { MoveToPosCmd } from "./commands/movetopos";
import { MoveToObjCmd } from "./commands/movetoobj";
import { AttackCmd } from "./commands/attack";
import { WASDMoveCmd } from "./commands/wasdmove";

// EPSILON constant (also defined on global in app.ts)
const EPSILON = 0.0001;

export class ActorObject extends GameObject {
    direction: number;
    speed: number;
    moveSpeed: number;
    activeCmd: any;
    nextCmd: any;
    useMoveCollision?: boolean;
    jumpVelocity: number = 0;
    isJumping: boolean = false;
    isDead: boolean = false;

    constructor(type: any, world: any) {
        super(type, world);
        this.direction = 0;
        this.speed = 0;
        this.moveSpeed = 550;
        this.activeCmd = null;
        this.nextCmd = null;
    }

    setVisible(visible: any) {
        this.pawn.rootObj.visible = visible;
    }

    _setNextCmd(cmd: any) {
        // We do not enter the newly submitted command immediately from here
        //   to allow callers of this function to assign event handlers for
        //   start or finish first.
        if (this.activeCmd) {
            this.activeCmd.wantInterrupt = true;
        }
        this.nextCmd = cmd;
        return cmd;
    }

    _stop() {
        return this._setNextCmd(new StopCmd(this));
    }

    _cancelCommands() {
        const activeCmd = this.activeCmd;

        // Clear both slots before leaving the active command. Movement-command
        // leave handlers use the empty queue to restore idle motion, and UI
        // listeners use the emitted leave event to remove click markers.
        this.activeCmd = null;
        this.nextCmd = null;
        this.speed = 0;

        if (activeCmd) {
            activeCmd.wantInterrupt = true;
            activeCmd._leave();
        }
    }

    _moveTo(x: any, y: any) {
        return this._setNextCmd(
            new MoveToPosCmd(this, new THREE.Vector2(x, y))
        );
    }

    _moveToObj(objectRef: any, distance: any) {
        if (!(objectRef instanceof GORef)) {
            console.warn("Reference passed to _moveToObj was not a GORef.");
            return;
        }
        return this._setNextCmd(new MoveToObjCmd(this, objectRef, distance));
    }

    _wasdMove(dirX: number, dirY: number) {
        return this._setNextCmd(new WASDMoveCmd(this, dirX, dirY));
    }

    jump() {
        if (this.isJumping) return;
        this.isJumping = true;
        this.jumpVelocity = 8; // Initial upward velocity
        if (this.pawn) {
            this.pawn.playJump1Motion(null, 1.0, 0);
        }
    }

    _attackObj(objectRef: any) {
        if (!(objectRef instanceof GORef)) {
            console.warn("Reference passed to _attackObj was not a GORef.");
            return;
        }
        return this._setNextCmd(new AttackCmd(this, objectRef));
    }

    _die(onFinish?: () => void) {
        if (this.isDead) return;

        this.isDead = true;
        this.activeCmd = null;
        this.nextCmd = null;
        this.speed = 0;

        if (this.pawn && this.pawn.playDieMotion) {
            this.pawn.playDieMotion(onFinish);
        } else if (onFinish) {
            onFinish();
        }
    }

    _revive() {
        this.isDead = false;
        this.activeCmd = null;
        this.nextCmd = null;
        this._stop();
    }

    setDirection(radians: any) {
        this.direction = radians;
        this.emit("moved");
    }

    update(delta: any) {
        if (this.isDead) {
            if (this.pawn) {
                this.pawn.setDirection(this.direction);
            }
            return super.update(delta);
        }

        let deltaLeft = delta;
        while (deltaLeft > EPSILON) {
            if (!this.activeCmd) {
                if (!this.nextCmd) {
                    this.nextCmd = new StopCmd(this);
                }
                this.activeCmd = this.nextCmd;
                this.nextCmd = null;
                this.activeCmd._enter();
            }
            deltaLeft = this.activeCmd.update(deltaLeft);
            if (this.activeCmd.isComplete) {
                this.activeCmd._leave();
                this.activeCmd = null;
            }
        }

        // Apply jump velocity and gravity
        if (this.useMoveCollision && this.world) {
            const gravity = 20; // units per second^2
            const highZ = this.world.findHighPoint(
                this.position.x,
                this.position.y,
                this.position.z + 1
            );

            if (this.isJumping) {
                this.jumpVelocity -= gravity * delta;
                const newZ = this.position.z + this.jumpVelocity * delta;

                if (highZ !== undefined && newZ <= highZ && this.jumpVelocity <= 0) {
                    // Landed
                    this.setPosition(this.position.x, this.position.y, highZ);
                    this.isJumping = false;
                    this.jumpVelocity = 0;
                    // Resume appropriate animation
                    if (this.pawn) {
                        if (this.activeCmd && this.activeCmd.setDirection) {
                            this.pawn.playRunMotion();
                        } else {
                            this.pawn.playIdleMotion();
                        }
                    }
                } else {
                    this.setPosition(this.position.x, this.position.y, newZ);
                }
            } else if (highZ !== undefined && this.position.z > highZ) {
                const fallAmount = Math.min(
                    this.position.z - highZ,
                    10 * delta
                );
                this.setPosition(
                    this.position.x,
                    this.position.y,
                    this.position.z - fallAmount
                );
            }
        }

        if (this.pawn) {
            this.pawn.setDirection(this.direction);
        }
        return super.update(delta);
    }
}

/**
 * @constructor
 */
